import { useSyncExternalStore, useState, type FocusEvent } from 'react';

const DEFAULT_POINTER_IDLE_MS = 1000;

interface UseAddElementSlotActivityResult {
    isSlotActive: boolean;
    handleFocusCapture: () => void;
    handleBlurCapture: (event: FocusEvent<HTMLElement>) => void;
}

interface PointerActivityStore {
    subscribe: (onStoreChange: () => void) => () => void;
    getSnapshot: () => boolean;
}

const pointerActivityStoreByIdleMs = new Map<number, PointerActivityStore>();

function createPointerActivityStore(idleMs: number): PointerActivityStore {
    const subscribers = new Set<() => void>();
    let isActive = true;
    let timeoutId: number | null = null;

    function notify(): void {
        subscribers.forEach((subscriber) => subscriber());
    }

    function setActive(nextActive: boolean): void {
        if (isActive === nextActive) {
            return;
        }
        isActive = nextActive;
        notify();
    }

    function clearIdleTimeout(): void {
        if (timeoutId === null || typeof window === 'undefined') {
            return;
        }
        window.clearTimeout(timeoutId);
        timeoutId = null;
    }

    function scheduleIdleTimeout(): void {
        if (typeof window === 'undefined') {
            return;
        }
        clearIdleTimeout();
        timeoutId = window.setTimeout(() => {
            timeoutId = null;
            setActive(false);
        }, idleMs);
    }

    function markActive(): void {
        setActive(true);
        scheduleIdleTimeout();
    }

    function startListening(): void {
        if (typeof window === 'undefined') {
            return;
        }
        markActive();
        window.addEventListener('pointermove', markActive, { passive: true });
        window.addEventListener('pointerdown', markActive, { passive: true });
    }

    function stopListening(): void {
        if (typeof window === 'undefined') {
            return;
        }
        clearIdleTimeout();
        window.removeEventListener('pointermove', markActive);
        window.removeEventListener('pointerdown', markActive);
    }

    function subscribe(onStoreChange: () => void): () => void {
        subscribers.add(onStoreChange);
        if (subscribers.size === 1) {
            startListening();
        }
        return () => {
            subscribers.delete(onStoreChange);
            if (subscribers.size === 0) {
                stopListening();
            }
        };
    }

    function getSnapshot(): boolean {
        return isActive;
    }

    return {
        subscribe,
        getSnapshot,
    };
}

function getPointerActivityStore(idleMs: number): PointerActivityStore {
    const cachedStore = pointerActivityStoreByIdleMs.get(idleMs);
    if (cachedStore) {
        return cachedStore;
    }

    const nextStore = createPointerActivityStore(idleMs);
    pointerActivityStoreByIdleMs.set(idleMs, nextStore);
    return nextStore;
}

function usePointerActivityWindow(idleMs: number): boolean {
    const pointerActivityStore = getPointerActivityStore(idleMs);
    return useSyncExternalStore(
        pointerActivityStore.subscribe,
        pointerActivityStore.getSnapshot,
        () => true
    );
}

export function useAddElementSlotActivity(
    isMenuOpen: boolean,
    keepActiveOnFocus: boolean,
    pointerIdleMs = DEFAULT_POINTER_IDLE_MS
): UseAddElementSlotActivityResult {
    const [isFocusWithin, setIsFocusWithin] = useState(false);
    const isPointerRecentlyActive = usePointerActivityWindow(pointerIdleMs);

    function handleFocusCapture(): void {
        setIsFocusWithin(true);
    }

    function handleBlurCapture(event: FocusEvent<HTMLElement>): void {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
            return;
        }

        setIsFocusWithin(false);
    }

    return {
        isSlotActive: isMenuOpen || (keepActiveOnFocus && isFocusWithin) || isPointerRecentlyActive,
        handleFocusCapture,
        handleBlurCapture,
    };
}
